import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  ArrowRight, ArrowLeft, CheckCircle, BookOpen, Target,
  Trophy, Sparkles, Loader2, GraduationCap, AlertCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicSettings } from '../../contexts/PublicSettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback questions used when none are configured in the admin panel
const DEFAULT_QUESTIONS = [
  {
    id: 1,
    category: 'Grammar',
    question: 'She ___ to the gym every morning.',
    options: ['go', 'goes', 'going', 'gone'],
  },
  {
    id: 2,
    category: 'Grammar',
    question: 'I ___ finished my homework when you called.',
    options: ["haven't", "hadn't", "didn't", "wasn't"],
  },
  {
    id: 3,
    category: 'Vocabulary',
    question: 'Choose the word that means "to make something larger or more extensive":',
    options: ['Reduce', 'Expand', 'Contract', 'Minimize'],
  },
  {
    id: 4,
    category: 'Grammar',
    question: 'If I ___ you, I would apologize.',
    options: ['am', 'was', 'were', 'be'],
  },
  {
    id: 5,
    category: 'Reading',
    question: '"The meeting has been postponed." This means the meeting has been:',
    options: ['Cancelled', 'Delayed', 'Started', 'Confirmed'],
  },
  {
    id: 6,
    category: 'Grammar',
    question: 'By next year, she ___ at the company for ten years.',
    options: ['will work', 'will have worked', 'works', 'worked'],
  },
  {
    id: 7,
    category: 'Vocabulary',
    question: 'The opposite of "optimistic" is:',
    options: ['Hopeful', 'Pessimistic', 'Realistic', 'Neutral'],
  },
  {
    id: 8,
    category: 'Grammar',
    question: 'The report ___ by the manager yesterday.',
    options: ['reviewed', 'was reviewed', 'is reviewed', 'reviewing'],
  },
  {
    id: 9,
    category: 'Vocabulary',
    question: '"She has a keen eye for detail." The word "keen" means:',
    options: ['Poor', 'Sharp', 'Lazy', 'Slow'],
  },
  {
    id: 10,
    category: 'Grammar',
    question: 'Neither the teacher nor the students ___ aware of the change.',
    options: ['was', 'were', 'is', 'being'],
  },
];

// Normalise a question from the API. The answer key intentionally remains server-side.
const normaliseQuestion = (q, index) => ({
  id: q.id ?? index,
  category: q.category,
  question: q.text ?? q.question,
  options: q.options,
});

const LEVEL_RESULTS = {
  beginner: {
    level: 'A1-A2',
    title: "You're at a Beginner / Elementary Level",
    description:
      "You're building your foundation. A structured and supportive learning plan will help you grow in confidence and consistency.",
    plan: 'Starter Plan / General English',
    icon: BookOpen,
    color: 'bg-[#4CAF50]',
    border: 'border-[#4CAF50]',
    badge: 'bg-[#4CAF50]/10 text-[#4CAF50]',
  },
  intermediate: {
    level: 'B1',
    title: "You're at an Intermediate Level",
    description:
      'You already have a working foundation. Now is the perfect time to improve fluency, accuracy, and confidence in real communication.',
    plan: 'Intermediate Plan',
    icon: Target,
    color: 'bg-[#1A2E6E]',
    border: 'border-[#1A2E6E]',
    badge: 'bg-[#1A2E6E]/10 text-[#1A2E6E]',
  },
  upperIntermediate: {
    level: 'B2',
    title: "You're at an Upper-Intermediate Level",
    description:
      "You're ready to refine your communication, improve precision, and use English more confidently for real-world and professional situations.",
    plan: 'Professional / Advanced Progress Plan',
    icon: Trophy,
    color: 'bg-[#F5A623]',
    border: 'border-[#F5A623]',
    badge: 'bg-[#F5A623]/10 text-[#C97D0A]',
  },
  advanced: {
    level: 'C1-C2',
    title: "You're at an Advanced Level",
    description:
      'You already communicate with confidence. Now the focus is refinement, precision, sophistication, and advanced fluency.',
    plan: 'Advanced / Intensive Plan',
    icon: Sparkles,
    color: 'bg-[#D72B2B]',
    border: 'border-[#D72B2B]',
    badge: 'bg-[#D72B2B]/10 text-[#D72B2B]',
  },
};

const CATEGORY_COLORS = {
  Grammar: 'bg-[#1A2E6E]/10 text-[#1A2E6E]',
  Vocabulary: 'bg-[#4CAF50]/10 text-[#4CAF50]',
  Reading: 'bg-[#F5A623]/10 text-[#C97D0A]',
};

// ─── Shell ────────────────────────────────────────────────────────────────────
const Shell = ({ children }) => (
  <div className="min-h-screen bg-gradient-to-br from-[#FAFAF7] via-[#FDF8F3] to-[#FAFAF7] flex items-center justify-center px-4 py-12">
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-20 left-10 w-72 h-72 bg-[#F5A623]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#1A2E6E]/5 rounded-full blur-3xl" />
    </div>
    <div className="w-full max-w-lg relative z-10">
      <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border border-[#1A2E6E]/10 overflow-hidden">
        <div className="px-8 pt-8 pb-2 flex justify-center">
          <Link to="/">
            <img src="/logo.png" alt="Frankly Talking English" className="h-12 w-auto" />
          </Link>
        </div>
        {children}
      </div>
    </div>
  </div>
);

export const StudentLevelTest = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { executeRecaptcha } = usePublicSettings();

  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [phase, setPhase] = useState('intro'); // intro | testing | submitting | result | error
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [continuing, setContinuing] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/public/level-test-questions`)
      .then(res => {
        const loaded = res.data;
        setQuestions(
          loaded.length > 0
            ? loaded.map(normaliseQuestion)
            : DEFAULT_QUESTIONS.map(normaliseQuestion)
        );
      })
      .catch(() => {
        setQuestions(DEFAULT_QUESTIONS.map(normaliseQuestion));
      })
      .finally(() => setLoadingQuestions(false));
  }, []);

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  const handleAnswer = (idx) => {
    setAnswers((prev) => ({ ...prev, [currentQuestion]: idx }));
  };

  const handleNext = async () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion((q) => q + 1);
      return;
    }
    // Last question answered — submit immediately
    setPhase('submitting');
    try {
      // Returns null when reCAPTCHA is disabled in admin settings (backend lets it through),
      // and a verification token once an admin configures reCAPTCHA.
      const recaptchaToken = await executeRecaptcha('level_test');
      const response = await axios.post(`${API_URL}/api/public/level-test`, {
        answers: questions.map((question, index) => ({
          question_id: String(question.id),
          selected_answer_index: answers[index],
        })),
        email: user?.email ?? null,
        recaptcha_token: recaptchaToken,
      });
      const levelToKey = { 'A1-A2': 'beginner', B1: 'intermediate', B2: 'upperIntermediate', 'C1-C2': 'advanced' };
      const levelResult = LEVEL_RESULTS[levelToKey[response.data.level]] || LEVEL_RESULTS.beginner;
      setResult({
        score: response.data.score,
        totalQuestions: response.data.total_questions,
        ...levelResult,
      });
      setPhase('result');
    } catch {
      setErrorMsg('Could not save your result. Please try again.');
      setPhase('error');
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) setCurrentQuestion((q) => q - 1);
  };

  const handleContinue = async () => {
    setContinuing(true);
    await refreshUser();
    navigate('/student/pending');
  };

  // ── Loading questions ──────────────────────────────────────────────────────
  if (loadingQuestions) {
    return (
      <Shell>
        <div className="p-8 text-center space-y-4">
          <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin mx-auto" />
          <p className="text-sm text-[#1A2E6E]/50">Loading test…</p>
        </div>
      </Shell>
    );
  }

  // ── Intro ──────────────────────────────────────────────────────────────────
  if (phase === 'intro') {
    return (
      <Shell>
        <div className="p-8 space-y-6">
          <div className="text-center">
            <div className="w-20 h-20 bg-[#1A2E6E]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <GraduationCap className="w-10 h-10 text-[#1A2E6E]" />
            </div>
            <h1 className="text-2xl font-bold text-[#1A2E6E] font-['Sora']">Level Assessment</h1>
            <p className="text-[#1A2E6E]/60 mt-2 text-sm">
              10 quick questions to find your English level and get a personalised plan recommendation.
            </p>
          </div>

          <div className="bg-[#F8F9FC] rounded-xl p-5 space-y-2.5">
            {[
              { label: 'Grammar', color: 'text-[#1A2E6E]' },
              { label: 'Vocabulary', color: 'text-[#4CAF50]' },
              { label: 'Reading comprehension', color: 'text-[#F5A623]' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3">
                <CheckCircle size={16} className={item.color} />
                <span className="text-sm text-[#1A2E6E]/70">{item.label}</span>
              </div>
            ))}
            <p className="text-xs text-[#1A2E6E]/40 pt-1">Takes approximately 5 minutes</p>
          </div>

          <button
            onClick={() => setPhase('testing')}
            className="w-full py-3.5 rounded-xl bg-[#D72B2B] text-white font-bold hover:bg-[#B71C1C] transition-colors flex items-center justify-center gap-2"
          >
            Start Level Test <ArrowRight size={18} />
          </button>

          <div className="pb-2 text-center">
            <Link to="/student/pending" className="text-sm text-[#1A2E6E]/40 hover:text-[#1A2E6E] transition-colors">
              ← Back
            </Link>
          </div>
        </div>
      </Shell>
    );
  }

  // ── Submitting ─────────────────────────────────────────────────────────────
  if (phase === 'submitting') {
    return (
      <Shell>
        <div className="p-8 text-center space-y-6">
          <div className="w-20 h-20 bg-[#1A2E6E]/10 rounded-full flex items-center justify-center mx-auto">
            <Loader2 className="w-10 h-10 text-[#1A2E6E] animate-spin" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#1A2E6E] font-['Sora']">Calculating your level…</h2>
            <p className="text-[#1A2E6E]/50 text-sm mt-1">Just a moment</p>
          </div>
        </div>
      </Shell>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (phase === 'error') {
    return (
      <Shell>
        <div className="p-8 text-center space-y-6">
          <div className="w-20 h-20 bg-[#D72B2B]/10 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle className="w-10 h-10 text-[#D72B2B]" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#1A2E6E] font-['Sora']">Something went wrong</h2>
            <p className="text-[#1A2E6E]/60 text-sm mt-1">{errorMsg}</p>
          </div>
          <button
            onClick={() => { setPhase('testing'); setCurrentQuestion(questions.length - 1); }}
            className="w-full py-3 rounded-xl bg-[#1A2E6E] text-white font-medium hover:bg-[#16275C] transition-colors"
          >
            Try again
          </button>
        </div>
      </Shell>
    );
  }

  // ── Result ─────────────────────────────────────────────────────────────────
  if (phase === 'result' && result) {
    const ResultIcon = result.icon;
    return (
      <Shell>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-8 space-y-6"
        >
          <div className="text-center">
            <div className={`w-20 h-20 rounded-full ${result.color} flex items-center justify-center mx-auto mb-4 shadow`}>
              <ResultIcon size={36} className="text-white" />
            </div>
            <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold ${result.badge} mb-3`}>
              {result.level}
            </span>
            <h2 className="text-xl font-bold text-[#1A2E6E] font-['Sora']">{result.title}</h2>
            <p className="text-[#1A2E6E]/60 text-sm mt-2 leading-relaxed">{result.description}</p>
          </div>

          <div className={`rounded-xl border-l-4 ${result.border} bg-[#F8F9FC] p-4`}>
            <p className="text-xs text-[#1A2E6E]/50 mb-0.5">Score</p>
            <p className="font-semibold text-[#1A2E6E]">{result.score} / {result.totalQuestions} correct</p>
            <p className="text-xs text-[#1A2E6E]/50 mt-2 mb-0.5">Recommended plan</p>
            <p className="font-semibold text-[#1A2E6E]">{result.plan}</p>
          </div>

          <button
            onClick={handleContinue}
            disabled={continuing}
            className="w-full py-3.5 rounded-xl bg-[#D72B2B] text-white font-bold hover:bg-[#B71C1C] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {continuing
              ? <><Loader2 size={18} className="animate-spin" /> Loading…</>
              : <>Continue <ArrowRight size={18} /></>
            }
          </button>
        </motion.div>
      </Shell>
    );
  }

  // ── Questions ──────────────────────────────────────────────────────────────
  const currentQ = questions[currentQuestion];
  const selectedAnswer = answers[currentQuestion];

  return (
    <Shell>
      <div className="p-8 space-y-6">
        {/* Progress */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs text-[#1A2E6E]/50">
              Question {currentQuestion + 1} of {questions.length}
            </span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${CATEGORY_COLORS[currentQ.category] || 'bg-[#1A2E6E]/10 text-[#1A2E6E]'}`}>
              {currentQ.category}
            </span>
          </div>
          <div className="w-full h-1.5 bg-[#E8EAF0] rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-[#D72B2B] rounded-full"
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        {/* Question card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.2 }}
            className="space-y-4"
          >
            <p className="font-semibold text-[#1A2E6E] text-base leading-snug">
              {currentQ.question}
            </p>

            <div className="space-y-2.5">
              {currentQ.options.map((option, idx) => (
                <button
                  key={idx}
                  onClick={() => handleAnswer(idx)}
                  className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all text-sm font-medium ${
                    selectedAnswer === idx
                      ? 'border-[#1A2E6E] bg-[#1A2E6E]/5 text-[#1A2E6E]'
                      : 'border-[#E8EAF0] bg-white text-[#1A2E6E]/80 hover:border-[#1A2E6E]/30'
                  }`}
                >
                  <span className={`inline-flex w-6 h-6 rounded-full border-2 items-center justify-center text-xs mr-3 ${
                    selectedAnswer === idx
                      ? 'border-[#1A2E6E] bg-[#1A2E6E] text-white'
                      : 'border-[#D1D5DB] text-[#9CA3AF]'
                  }`}>
                    {String.fromCharCode(65 + idx)}
                  </span>
                  {option}
                </button>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex justify-between items-center pt-2">
          <button
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            className="flex items-center gap-1.5 text-sm text-[#1A2E6E]/50 hover:text-[#1A2E6E] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowLeft size={16} /> Previous
          </button>

          <button
            onClick={handleNext}
            disabled={selectedAnswer === undefined}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#1A2E6E] text-white text-sm font-semibold hover:bg-[#16275C] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {currentQuestion === questions.length - 1 ? 'See my result' : 'Next'}
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </Shell>
  );
};

export default StudentLevelTest;
